import { PayPeriodSettings } from "../operations/PayPeriodSettings";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plug, Plus, RefreshCw, Trash2 } from "lucide-react";
import { xeroApi } from "../../backend/api/xero";
import type {
  Integration,
  Schedule,
  SchedulerHealth,
  XeroStatus,
  XeroConnection,
} from "../../backend/api/xero";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Page, PageHeader } from "../../components/patterns/page";
import { EmptyState } from "../../components/patterns/states";
import { StatusBadge } from "../../components/patterns/status";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import XeroConnectionAccess from "./XeroConnectionAccess";
import XeroEmployeeImportDialog from "./XeroEmployeeImportDialog";
import { Stat, formatDateTime } from "./shared";

const MAX_RUN_TIMES = 4;

/** "20 seconds" or "1 minute" reads better than a bare number in a stat. */
const describeInterval = (seconds: number) => {
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }
  return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
};

/** The Xero connection itself: who we are connected to, and how to reconnect. */
export default function XeroSettings() {
  const [status, setStatus] = useState<XeroStatus | null>(null);
  const [scheduler, setScheduler] = useState<SchedulerHealth | null>(null);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importConnection, setImportConnection] = useState<XeroConnection | null>(null);
  const [syncMessage, setSyncMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [statusResult, schedulerResult, integrationsResult] = await Promise.all([
      xeroApi.status(),
      xeroApi.schedulerHealth(),
      xeroApi.listIntegrations(),
    ]);
    setLoading(false);

    if (statusResult.error) {
      setError(statusResult.error.message || "Could not load the Xero connection.");
      return;
    }

    setError("");
    setStatus(statusResult.data);
    setScheduler(schedulerResult.data);
    setIntegrations(integrationsResult.data || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Page>
      <PageHeader title="Xero" breadcrumbs={[{ label: "Connections", to: "/integrations" }]}
        actions={<Button asChild variant="outline"><Link to="/integrations/xero">View invoice syncs</Link></Button>} />

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {syncMessage && <Alert><AlertTitle>Employee sync complete</AlertTitle><AlertDescription>{syncMessage}</AlertDescription></Alert>}

      {!loading && !status?.configured && <Alert>
        <AlertTitle>Setup required</AlertTitle>
        <AlertDescription>Xero is not configured for this environment. Connection access can be reviewed below.</AlertDescription>
      </Alert>}
      <Tabs defaultValue="permissions" className="flex flex-col gap-4">
        <TabsList className="w-fit" aria-label="Xero settings">
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
          <TabsTrigger value="connection">Connection</TabsTrigger>
          <TabsTrigger value="automation">Automation</TabsTrigger>
        </TabsList>
        <TabsContent value="permissions" forceMount className="flex flex-col gap-5 data-[state=inactive]:hidden">
          {!status?.connections.length && <Card>
            <CardHeader><CardTitle className="text-base">New connection access</CardTitle>
              <CardDescription>Choose what the portal can read from and write to Xero. These choices take effect after connecting.</CardDescription>
            </CardHeader>
            <CardContent><XeroConnectionAccess capabilities={status?.capabilities || []} loading={loading} configured={Boolean(status?.configured)} onSaved={load} onError={setError} /></CardContent>
          </Card>}
          {status?.connections.map(connection => <Card key={connection.tenant_id}>
            <CardHeader><CardTitle className="text-base">{connection.tenant_name}</CardTitle>
              <CardDescription>What the portal can read from and write to this organisation.</CardDescription>
            </CardHeader>
            <CardContent><XeroConnectionAccess capabilities={status.capabilities || []} connection={connection} configured={Boolean(status.configured)} onSaved={load} onError={setError} /></CardContent>
          </Card>)}
        </TabsContent>
        <TabsContent value="connection" className="flex flex-col gap-5">
          <Card><CardHeader><CardTitle className="flex items-center gap-2 text-base"><Plug className="h-5 w-5" />Connected organisations</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-4">
              {!status?.connections.length && <EmptyState title="No organisation connected" description="Review connection access in Permissions, then continue to Xero to authorise the connection." />}
              {status?.connections.map(connection => <div key={connection.tenant_id} className="flex flex-col gap-3 rounded-lg border p-4">
                <div className="flex flex-wrap items-center gap-2"><h2 className="text-base">{connection.tenant_name}</h2><StatusBadge tone={connection.status === "connected" ? "success" : "warning"}>{connection.status.replaceAll("_", " ")}</StatusBadge></div>
                <p className="text-sm text-muted-foreground">Import employees into pending portal accounts and review their payroll mappings. Send invitations separately from People & access.</p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => { setSyncMessage(""); setImportConnection(connection); }} disabled={connection.status !== "connected"}>
                    <RefreshCw className="mr-2 h-4 w-4" />Import employees
                  </Button>
                  <Button variant="outline" asChild><Link to="/user-management">Open People & access</Link></Button>
                </div>
              </div>)}
              {!!status?.capabilities?.filter(feature => feature.access_key === null).length && <details className="text-sm text-muted-foreground"><summary className="cursor-pointer">Read-only lookups used by the integration</summary><dl className="mt-2 space-y-3">{status.capabilities.filter(feature => feature.access_key === null).map(feature => <div key={feature.id}><dt className="font-medium text-foreground">{feature.name}</dt><dd>{feature.read_description}</dd></div>)}</dl></details>}
              <details className="text-sm text-muted-foreground"><summary className="cursor-pointer">About Xero authorisation</summary><p className="mt-2">Xero consent grants invoice and timesheet access, with read-only employee, payroll-calendar and organisation lookup. Portal access limits each feature separately. Xero's granted permissions are an additional limit; existing project and workflow permissions still apply.</p></details>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="automation" className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scheduler</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Stat label="State">
              {!scheduler ? (
                "Checking..."
              ) : !scheduler.enabled ? (
                <span className="text-tone-warning-foreground">Turned off by configuration</span>
              ) : scheduler.stale ? (
                // The process answered this request, so the API is up. If the
                // heartbeat is still old, the loop behind it has stopped and
                // nothing will run until it is restarted.
                <span className="text-tone-danger-foreground">
                  Not running: last checked{" "}
                  {describeInterval(scheduler.seconds_since_tick)} ago
                </span>
              ) : (
                <span className="text-tone-success-foreground">Running</span>
              )}
            </Stat>
            <Stat label="Last checked">
              {scheduler ? formatDateTime(scheduler.last_tick_at) : "-"}
            </Stat>
            <Stat label="Checks every">
              {scheduler ? describeInterval(scheduler.tick_seconds) : "-"}
            </Stat>
          </dl>
        </CardContent>
      </Card>

      <PayPeriodSettings />

      {integrations.map((integration) => (
        <ScheduleCard
          key={integration.id}
          integrationId={integration.id}
          name={integration.name}
          schedule={integration.schedule}
          onSaved={() => load()}
          onError={setError}
        />
      ))}
        </TabsContent>
      </Tabs>
      {importConnection && <XeroEmployeeImportDialog connection={importConnection}
        onClose={() => setImportConnection(null)}
        onImported={(result) => setSyncMessage(`${result.created} accounts created, ${result.synced} existing accounts synced. Open People & access to send invitations.`)} />}
    </Page>
  );
}

/**
 * The times one sync runs itself, on a single line.
 *
 * A live schedule cannot have its times edited underneath it, so editing walks
 * through turning it off and offers to turn it back on afterwards. Doing that
 * silently would be worse: someone would change the times and never notice the
 * sync had stopped running.
 */
function ScheduleCard({
  integrationId,
  name,
  schedule,
  onSaved,
  onError,
}: {
  integrationId: string;
  name: string;
  schedule: Schedule | null;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [times, setTimes] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");
  const [confirmingTurnOff, setConfirmingTurnOff] = useState(false);
  /** Set when this edit turned the schedule off, so it can offer to undo that. */
  const [turnedOffToEdit, setTurnedOffToEdit] = useState(false);
  const [savedTimes, setSavedTimes] = useState<string[]>([]);

  const saved = schedule?.run_times || [];
  const enabled = Boolean(schedule?.enabled);

  useEffect(() => {
    // Only follow the server while the dialog is closed, or a refresh landing
    // mid-edit would wipe what someone is typing.
    if (!editing) setTimes(saved);
  }, [schedule, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty =
    times.length !== saved.length || times.some((time, index) => time !== saved[index]);

  const write = async (nextEnabled: boolean, nextTimes: string[]) => {
    const { error } = await xeroApi.saveSchedule(integrationId, {
      enabled: nextEnabled,
      run_times: nextTimes,
    });
    if (error) {
      onError(error.message || "Could not save the schedule.");
      return false;
    }
    return true;
  };

  const openEditor = () => {
    setTimes(saved);
    setProblem("");
    setEditing(true);
  };

  const requestEdit = () => {
    if (enabled) {
      setConfirmingTurnOff(true);
      return;
    }
    setTurnedOffToEdit(false);
    openEditor();
  };

  const turnOffAndEdit = async () => {
    setSaving(true);
    const ok = await write(false, saved);
    setSaving(false);
    if (!ok) return;

    setTurnedOffToEdit(true);
    setConfirmingTurnOff(false);
    openEditor();
    onSaved();
  };

  const closeEditor = () => {
    // Anything typed and not saved would be lost silently otherwise.
    if (dirty && !window.confirm("Discard your unsaved changes to the run times?")) {
      return;
    }
    setTimes(saved);
    setProblem("");
    setEditing(false);
  };

  const save = async () => {
    const entered = times.filter(Boolean);
    // Ascending order is not a preference: an out-of-order list reads as a
    // mistake, and two runs in the same minute would only queue behind each
    // other for no purpose.
    const cleaned = Array.from(new Set(entered)).sort();

    if (cleaned.length !== entered.length) {
      setProblem("Each run time must be distinct.");
      return;
    }

    setProblem("");
    setSaving(true);
    // Coming from the toggle, the intent was to turn it on, so saving times
    // completes that rather than leaving it off and needing a second click.
    const turnOn = !enabled && !turnedOffToEdit && cleaned.length > 0;
    const ok = await write(turnOn, cleaned);
    setSaving(false);
    if (!ok) return;

    setSavedTimes(cleaned);
    setEditing(false);
    onSaved();
  };

  const reenable = async () => {
    if (savedTimes.length === 0) {
      onError("Add at least one run time before turning the schedule back on.");
      setTurnedOffToEdit(false);
      return;
    }
    setSaving(true);
    const ok = await write(true, savedTimes);
    setSaving(false);
    setTurnedOffToEdit(false);
    if (ok) onSaved();
  };

  // Asked once the times are saved and the dialog has closed.
  const askingToReenable = turnedOffToEdit && !editing && !confirmingTurnOff;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2 py-4">
        <CardTitle className="text-base">{name} schedule (AEST)</CardTitle>

        <span className="text-sm text-muted-foreground">
          {saved.length ? saved.join(", ") : "No times set"}
        </span>

        <Button variant="outline" size="sm" onClick={requestEdit}>
          Edit times
        </Button>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{enabled ? "On" : "Off"}</span>
          <Switch
            checked={enabled}
            disabled={saving}
            aria-label={`Run ${name} on a schedule`}
            onCheckedChange={(next) => {
              // Turning on with no times is a missing step, not a failure. Open
              // the editor where the times are set rather than reporting an
              // error at the top of the page and leaving the person to find it.
              if (next && saved.length === 0) {
                setProblem("Set at least one run time, then save to turn the schedule on.");
                setTimes([]);
                setEditing(true);
                return;
              }
              write(next, saved).then((ok) => ok && onSaved());
            }}
          />
        </div>
      </CardContent>

      <Dialog
        open={confirmingTurnOff}
        onOpenChange={(open) => !open && setConfirmingTurnOff(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn the schedule off to edit it?</DialogTitle>
            <DialogDescription>
              {name} is scheduled to run at {saved.join(", ")} AEST. Its times cannot be
              changed while it is on, so it will be turned off first. Nothing will run
              automatically until you turn it back on, which you will be offered once your
              changes are saved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setConfirmingTurnOff(false)}
            >
              Cancel
            </Button>
            <Button disabled={saving} onClick={turnOffAndEdit}>
              {saving ? "Turning off..." : "Turn off and edit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing} onOpenChange={(open) => !open && closeEditor()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{name} run times (AEST)</DialogTitle>
          </DialogHeader>

          {problem && <p className="text-sm text-red-600">{problem}</p>}

          <div className="flex flex-col gap-2">
            {times.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No times yet. Add one to schedule this sync.
              </p>
            )}

            {times.map((time, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="time"
                  className="w-36"
                  value={time}
                  aria-label={`Run time ${index + 1}`}
                  onChange={(event) =>
                    setTimes((current) =>
                      current.map((value, at) => (at === index ? event.target.value : value)),
                    )
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove run time ${index + 1}`}
                  onClick={() => setTimes((current) => current.filter((_, at) => at !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              className="w-fit"
              disabled={times.length >= MAX_RUN_TIMES}
              title={
                times.length >= MAX_RUN_TIMES
                  ? `A sync can run at most ${MAX_RUN_TIMES} times a day`
                  : "Add a run time"
              }
              onClick={() => setTimes((current) => [...current, "01:00"])}
            >
              <Plus className="h-4 w-4" />
              Add time
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={saving} onClick={closeEditor}>
              Cancel
            </Button>
            <Button disabled={saving || !dirty} onClick={save}>
              {saving ? "Saving..." : "Save times"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={askingToReenable}
        onOpenChange={(open) => !open && setTurnedOffToEdit(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn the schedule back on?</DialogTitle>
            <DialogDescription>
              {name} is off because you edited its times. It will not run automatically
              until it is turned back on.
              {savedTimes.length > 0 && ` New times: ${savedTimes.join(", ")} AEST.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setTurnedOffToEdit(false)}
            >
              Leave it off
            </Button>
            <Button disabled={saving} onClick={reenable}>
              {saving ? "Turning on..." : "Turn back on"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
